import { Box, CircularProgress, Modal, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { Form, Formik } from "formik";
import { getAPICall, putAPICall } from "lib/callApi";
import { FETCH_KPI_ACTIVITIES, USERS_KPIS } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { compnayUsersAction } from "../../../redux/slice/companyUsers";
import EditKPITable from "../components/EditKPITable";

function AssignUserModal({ open, handleClose, editModalData, payload }) {
  const [usersActivities, setUsersActivities] = useState([]);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [kpiActivities, setKpiActivities] = useState([]);
  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
    borderRadius: "0.5rem",
    width: isMobileDisplay ? "90%" : 750,
    border: "none",
    outline: "none",
  };

  const dispatch = useDispatch();

  const changeAssignedKPIs = async (body) => {
    setIsRequestApiLoading(true);
    const kpisToDelete = usersActivities
      .filter((userKpi) => !body.kpiActivities.some((newKpi) => newKpi.id === userKpi.id))
      .map((kpi) => kpi.id);
    try {
      const response = await putAPICall(FETCH_KPI_ACTIVITIES, true, {
        activities: body.kpiActivities.map((_) => ({
          id: _.id,
          title: _?.name,
          count: _.count,
          points: _.points,
        })),
        userId: editModalData.user,
        activitiesToBeDeleted: kpisToDelete,
      });
      if (!response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "error",
            text: "Could not create change kpi activities, Please try again!",
          }),
          {
            variant: "custom",
          }
        );

        return;
      }
      enqueueSnackbar(
        JSON.stringify({
          icon: "success",
          title: "Success",
          text: "Kpi activities for this user updated succesfully!",
        }),
        {
          variant: "custom",
        }
      );
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "error",
          text: "Could not create change kpi activities, Please try again!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      handleClose();
      setIsRequestApiLoading(false);
    }
  };
  useEffect(() => {
    dispatch(compnayUsersAction.getCompanyUsersRequest(payload));
  }, []);

  const getUsersKpis = async () => {
    setIsRequestApiLoading(true);
    try {
      const response = await getAPICall(`${USERS_KPIS}`, true, { user: editModalData.user });

      if (response?.data?.success) {
        const formated = [];
        response.data.data.forEach((_) => {
          if (_?.KpiActivity?.name && _?.KpiActivity?.id) {
            formated.push({
              name: _.KpiActivity?.name,
              id: _.KpiActivity?.id,
              count: _.count,
              points: _.points,
            });
          }
        });
        setUsersActivities(formated);
        setIsRequestApiLoading(false);
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something went wrong",
          }),
          {
            variant: "custom",
          }
        );
        setIsRequestApiLoading(false);
        handleClose();
      }
    } catch (error) {
      handleClose();
      setIsRequestApiLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  useEffect(() => {
    getUsersKpis();
    getAPICall(FETCH_KPI_ACTIVITIES, true).then((res) => {
      setKpiActivities(res.data.data);
    });
  }, []);

  // eslint-disable-next-line func-names
  Yup.addMethod(Yup.array, "unique", function (message, mapper = (a) => a) {
    // eslint-disable-next-line react/no-this-in-sfc
    return this.test("unique", message, (list) => list.length === new Set(list.map(mapper)).size);
  });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {isRequestApiLoading ? (
          <Stack alignItems="center">
            <CircularProgress
              sx={{
                position: "fixed",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
              }}
              color="info"
              size="10vh"
            />
          </Stack>
        ) : (
          ""
        )}
        <Typography
          id="modal-modal-title"
          variant="h5"
          component="h2"
          sx={{ marginBottom: "1rem" }}
        >
          {editModalData.name}
        </Typography>
        <MDBox>
          <Box
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
            }}
          >
            <Formik
              style={{ marginTop: "2rem" }}
              initialValues={{
                kpiActivities:
                  usersActivities.length === 0
                    ? [{ id: null, count: 1, name: "", points: 1 }]
                    : usersActivities,
              }}
              validationSchema={Yup.object({
                kpiActivities: Yup.array()
                  .of(
                    Yup.object().shape({
                      // id: Yup.number().required("Please select KPI").typeError("Please select KPI"),
                      name: Yup.string()
                        .required("Please select KPI")
                        .typeError("Please select KPI"),
                      points: Yup.number()
                        .min(1, "Invalid point")
                        .max(999, "Invalid point")
                        .typeError("Invalid point"),
                      count: Yup.number()
                        .min(1, "Invalid kpiNo")
                        .max(999, "Invalid kpiNo")
                        .typeError("Invalid kpiNo"),
                    })
                  )
                  .unique("New KPI names cannot be same.", (a) => a?.name?.trim())
                  .required("Please select KPI Activity"),
              })}
              enableReinitialize
              validateOnChange
              onSubmit={changeAssignedKPIs}
            >
              {({ errors, values, touched, setValues, setFieldValue }) => (
                <Form>
                  <EditKPITable
                    kpiActivities={kpiActivities}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                    values={values}
                    touched={touched}
                    errors={errors}
                    isMobileDisplay={isMobileDisplay}
                  />
                  <MDBox sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                    <MDButton
                      color="gray80"
                      type="button"
                      onClick={handleClose}
                      style={{ marginRight: "10px" }}
                    >
                      Cancel
                    </MDButton>
                    <MDButton type="submit" color="primaryRed" disabled={isRequestApiLoading}>
                      {isRequestApiLoading ? "Loading..." : "Save"}
                    </MDButton>
                  </MDBox>
                </Form>
              )}
            </Formik>
          </Box>
        </MDBox>
      </Box>
    </Modal>
  );
}

AssignUserModal.defaultProps = {
  open: false,
  editModalData: { user: null, isModalOpen: false },
  handleClose: () => {},
};

AssignUserModal.propTypes = {
  open: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  editModalData: PropTypes.any,
  handleClose: PropTypes.func,
};

export default AssignUserModal;
